// useDemoSeed — seed the demo dataset into the spine on first app entry (local mode).
//
// RELOCATED from the retired /borefields launcher (BorefieldList): the launcher used to host this
// useEffect, but it was the app's LANDING page so the seed fired on entry. With the launcher removed and
// the app landing on /pads, the seed lives here and is mounted in the always-present protected app shell
// (RootLayout), so demo data still seeds regardless of which page the user lands on.
//
// Fires ONCE while the store is empty: it upserts the generated Borefield into the canonical spine (the
// facade projects it straight back). No engineering math — pure demo scaffolding (Charter Abs-2).

import { useEffect } from "react";
import { useBorefieldData } from "../../borefields/hooks/useBorefieldData";
import { upsertBorefieldsIntoSpine } from "../store/borefieldFacade";
import { padsStore, wellsStore, padJobsStore } from "../store/spineStore";
import { generateOilfieldTestData } from "../../../utils/testDataGenerator";
import { CORPUS_MODE } from "../store/corpusMode";
import { resetAfeDemo } from "../../jobs/seedAfeDemo";
import type { Pad } from "../types";
import type { StimulationEntry } from "../../stimulation/types";
import {
  writeJobTimeLog,
  clearAllJobTimeLogs,
} from "../../timeline/store/jobTimeLogStore";
import type { Borefield } from "../../borefields/types";
import type { Job } from "../../jobs/types";

/** Persist the generated pad's time logs into the SHARDED time-log store.
 *
 *  `Job.timeLog` is canonical but NOT co-persisted with the well: it lives in its
 *  own localStorage shard (`oxbo-timeline-v8:<jobId>`) so time-log edits don't
 *  write-amplify the wells blob. `upsertBorefieldsIntoSpine` writes the wells
 *  blob only — so a generated job's timeLog would be dropped on the floor and
 *  `useTimeline` (which reads the shard, and refuses to seed a real job per
 *  Abs-7) would render an EMPTY timeline. This is the seam that persists it.
 *
 *  Demo scaffolding only: it writes exactly the logs the generator just built. */
/** Remove every Job.timeLog before the pad goes into the wells blob.
 *
 *  THE TIME LOG MUST NOT RIDE THE WELLS BLOB. It is canonically Job.timeLog but
 *  PHYSICALLY a per-job localStorage shard, precisely so a time-log edit does not
 *  rewrite the whole wells record. Leaving the generated logs attached to the job
 *  literals put them in BOTH places: measured, the generated pad went from 1.22 MB
 *  to 2.54 MB, with the same 1.32 MB written again across the shards — ~3.9 MB
 *  against a ~5 MB localStorage quota. The wells write then failed while the pads
 *  write succeeded, and the app rendered PADS WITH ZERO WELLS.
 *
 *  The generator still attaches timeLog so the graph it returns is coherent and
 *  testable; this strips it at the single point where the pad is persisted. */
/** Report families that are SERVED, not stored. A daily report family has one record per rig day,
 *  so it scales with the campaign rather than with the well count: the drilling cost log alone is
 *  1.00 MB across 258 reports on an 11-well pad, against a 2 MB budget for the whole wells blob.
 *  Six more families are planned. They live in the companion database and arrive per-well through
 *  `/api/wells/:id/bundle` (DECISIONS.md 2026-09-03). */
const SERVED_REPORT_TYPES = new Set(["cost-log"]);

export function stripTimeLogs(borefield: Borefield): Borefield {
  // Preserve SHAPE where there is nothing to strip: returning `bores: []` for a
  // borefield that never had a `bores` key invents structure the caller did not
  // supply, which is both wrong and a silent change to what gets persisted.
  if (!borefield.bores?.length) return borefield;
  // The PAD-scoped stimulation stream (S6 shared services — wireline, plugs, coil,
  // flowback, spread rig-up, safety meetings) is served from the companion database on
  // the pad row, exactly like the per-job stream below. ~0.9 MB on an 11-well pad; left
  // in, it alone puts the wells blob over budget.
  const { stimulationPad: _padStream, ...rest } =
    borefield as unknown as Record<string, unknown>;
  return {
    ...(rest as unknown as Borefield),
    bores: borefield.bores.map((bore) => {
      const jobs = (bore as unknown as { jobs?: Job[] }).jobs;
      if (!jobs?.length) return bore;
      return {
        ...bore,
        jobs: jobs.map((job) => {
          const served = (job.reports ?? []).some((r) =>
            SERVED_REPORT_TYPES.has(
              (r as unknown as { reportType?: string }).reportType ?? "",
            ),
          );
          if (!job.timeLog && !job.stimulation && !served) return job;
          const copy: Job = { ...job };
          delete copy.timeLog;
          // The S6 stimulation stream is the same shape of problem as the time log:
          // canonically `Job.stimulation`, PHYSICALLY served from the companion
          // database through the well bundle. Left in, it put the wells blob at
          // 3.57 MB against a 2 MB budget — the exact regression this file's header
          // describes, where the wells write fails silently and the app renders pads
          // with zero wells. The PO ruled on 2026-09-04 that all demo data lives in
          // the database, not localStorage; this is where that is enforced.
          delete copy.stimulation;
          if (served) {
            // Preserve SHAPE: a job that had reports keeps a `reports` array, emptied of the
            // served families rather than deleted. Removing the key would change what a consumer
            // reading `job.reports.length` sees, which is a different bug from the one being fixed.
            copy.reports = (job.reports ?? []).filter(
              (r) =>
                !SERVED_REPORT_TYPES.has(
                  (r as unknown as { reportType?: string }).reportType ?? "",
                ),
            );
          }
          return copy;
        }),
      };
    }),
  } as unknown as Borefield;
}

function seedGeneratedTimeLogs(borefield: Borefield): void {
  for (const bore of borefield.bores ?? []) {
    for (const job of (bore as { jobs?: Job[] }).jobs ?? []) {
      if (job.id && job.timeLog) writeJobTimeLog(job.id, job.timeLog);
    }
  }
}

export function useDemoSeed(): void {
  const { borefields, isLocalMode } = useBorefieldData();
  useEffect(() => {
    // CORPUS-MODE INTERLOCK (specialist review 2026-08-28). `borefields` comes from
    // `projectBorefields`, which SKIPS every pad without a `legacyBorefield` carry — corpus pads
    // have none, so on a fresh corpus-mode load this counted 0 and seeded a generated oilfield
    // into the very store built to keep localStorage empty. `isLocalMode` does not gate it:
    // corpus mode runs WITH local mode on, they are independent flags.
    if (!CORPUS_MODE && isLocalMode && borefields.length === 0) {
      // Demo scaffolding must never crash the app: a generator/upsert failure is logged and swallowed
      // (the user just gets no demo data) rather than propagated to an error boundary.
      try {
        const generated = generateOilfieldTestData();
        upsertBorefieldsIntoSpine([stripTimeLogs(generated)]);
        seedGeneratedTimeLogs(generated);
        seedGeneratedStimulationPad(generated);
      } catch (err) {
        console.error("useDemoSeed: failed to seed demo data", err);
      }
    }
  }, [isLocalMode, borefields.length]);
}

/** Attach the generated pad's SHARED-SERVICES stimulation stream to the pad record.
 *
 *  Mirrors `seedGeneratedTimeLogs` exactly: `stripTimeLogs` removes the stream before
 *  the spine is persisted (1.37 MB on an 11-well pad — it alone put the wells blob over
 *  budget), so without this the common band has nothing to render and 2,231 entries are
 *  written and read by nobody.
 *
 *  TRANSIENT, deliberately. `setTransient` holds the stream in memory without writing it
 *  to localStorage, the same contract the corpus well detail uses for its ~600 KB
 *  bundles. That is the whole point: the data lives in the database (PO 2026-09-04), and
 *  this is only the in-memory hand-off for the local demo path. */
function seedGeneratedStimulationPad(generated: Borefield): void {
  const stream = (generated as unknown as Record<string, unknown>)
    .stimulationPad as StimulationEntry[] | undefined;
  if (!stream?.length) return;
  const pads = padsStore.read();
  const target = pads.find((p) => p.id === generated.id);
  if (!target) return;
  (
    padsStore as unknown as { setTransient?: (r: Pad[]) => void }
  ).setTransient?.([{ ...target, stimulationPad: stream }]);
}

/** Reset the local demo dataset to a FRESH generated pad: clear the pad / well / pad-job spine and
 *  re-seed a new demo pad. The MANUAL counterpart to useDemoSeed's once-while-empty guard — since the
 *  seed never re-fires while data persists, this lets a user pull in regenerated demo data (e.g. after
 *  the generator gains new casing programs) without hand-clearing localStorage. Local-mode / demo only;
 *  it does NOT touch user-authored feature stores (completions, cellars) — those simply orphan, as with
 *  any full demo reset. */
export function reloadDemoData(): void {
  // Refuses in corpus mode. `setAll([])` infers a tombstone from every absent base record, so this
  // deleted all 2,538 corpus wells into the overlay PERSISTENTLY, with no UI able to undo it
  // (specialist review 2026-08-28). The store now also refuses an implicit mass delete, but the
  // button should never reach that guard — and its confirm copy says "Replace ALL local pads,
  // wells, and campaigns", which the read-only corpus is not.
  if (CORPUS_MODE) {
    console.warn(
      "reloadDemoData: refused — the well corpus is active and is read-only. " +
        "Switch off corpus mode in Settings to reset local demo data.",
    );
    return;
  }
  padsStore.setAll([]);
  wellsStore.setAll([]);
  padJobsStore.setAll([]);
  // Also clear the demo AFE stores (RM-14): a fresh spine mints new job/pad ids, so stale AfeLinks
  // would dangle and the seed's guard would block re-seeding → empty AFE program. Clear + let the
  // AFE seed re-fire against the new spine.
  resetAfeDemo();
  // Time logs are sharded per job under their own localStorage keys, so clearing the
  // spine above does NOT clear them — and since a fresh spine mints new job ids, every
  // prior shard would orphan permanently and eat the storage quota over repeated
  // reloads. Sweep them by prefix BEFORE re-seeding.
  clearAllJobTimeLogs();
  const generated = generateOilfieldTestData();
  upsertBorefieldsIntoSpine([stripTimeLogs(generated)]);
  seedGeneratedTimeLogs(generated);
  seedGeneratedStimulationPad(generated);
}
